import {
  cioFacetsToUrlFilters,
  cioSizeFacetsToFilterPreferences,
  MAX_FACET_COUNT,
  urlFiltersToCioFacets,
} from '~/grid/utils/cioUtils';
import { getBrandValue, isSavage } from '~/shared/utils/brandNameHelper';
import { sizeByKey } from '~/shared/utils/sizeConstants';
import { parseBraSizes } from '~/techstyle-shared/react-components/src/utils/sizes';
import slugify from '~/techstyle-shared/react-components/src/utils/slugify';

import { GRID_TYPES } from '../hooks/useGridInputs';

import { getNewQueryParamString, QueryParamBehavior } from './routing';

/**
 * Checks if the given facet name is a size facet.
 * Specific to Constructor data shape.
 *
 * @param {string} cioFacetName - The name of the facet to check.
 * @return {boolean} - Returns true if the facet name is a size facet.
 */
export const isCioSizeFacet = (cioFacetName) => {
  return cioFacetName?.startsWith('available_size_');
};

/**
 * Checks if the given facet name is a color facet.
 * Specific to Constructor data shape.
 *
 * @param {string} cioFacetName - The name of the facet to check.
 * @return {boolean} - Returns true if the facet name is a color facet.
 */
export const isCioColorFacet = (cioFacetName) => {
  const colorFacets = ['tag_color_list', 'tag_color_label'];
  return colorFacets.includes(cioFacetName);
};

/**
 * Checks if the given facet name is a custom bra filter.
 * This is specific to the Savage brand.
 *
 * @param {string} cioFacetName - The name of the facet to check.
 * @return {boolean} - Returns true if the facet name is a custom bra filter.
 */
export const isCustomBraFilter = (cioFacetName) => {
  return isSavage() && cioFacetName === 'available_size_bra';
};

/**
 * Updates the selected filters in the URL params, which we use as the source of truth for state management.
 * All data fetching is based on the URL params.
 *
 * @param {string} facetName - The name of the facet to update.
 * @param {string} facetOption - The option of the facet to update.
 * @param {Object} router - The router object for navigation.
 * @param {QueryParamBehavior} queryParamBehavior - The behavior for updating the query params. Default is TOGGLE.
 * @returns {void}
 */
export const updateFiltersInUrlParams = ({
  facetName,
  facetOption,
  router,
  queryParamBehavior = QueryParamBehavior.TOGGLE,
}) => {
  const [aspathname, query] = router.asPath.split('?');
  const newQueryParamString = getNewQueryParamString({
    query,
    newQueryParams: { [facetName]: facetOption },
    queryParamBehavior,
  });

  if (!newQueryParamString) {
    router.replace(router.pathname, aspathname, {
      shallow: true,
    });
    return;
  }

  router.replace(router.pathname, `${aspathname}?${newQueryParamString}`, {
    shallow: true,
  });
};

/**
 * Clears all CIO facets from the URL params, preserving other query params.
 * This is used when the user clears all filters.
 *
 * @param {Object} router - The router object for navigation.
 * @param {Array} allCioFacetNames - An array of all CIO facet names to be cleared.
 * @returns {void}
 */
export const clearAllCioFacetsFromUrlParams = ({
  router,
  allCioFacetNames,
}) => {
  const [aspathname, query] = router.asPath.split('?');
  const urlParams = new URLSearchParams(query);

  // Only remove cioFacets from query params
  // Preserve other query params like tabId and utm params
  const preservedQueryParams = {};
  urlParams.forEach((value, key) => {
    const mappedKey = urlFiltersToCioFacets[key] || key;

    if (!allCioFacetNames.includes(mappedKey)) {
      preservedQueryParams[mappedKey] = value;
    }
  });

  const newQueryParamString = new URLSearchParams(
    preservedQueryParams
  ).toString();

  if (!newQueryParamString) {
    router.replace(router.pathname, aspathname, {
      shallow: true,
    });
    return;
  }

  router.replace(router.pathname, `${aspathname}?${newQueryParamString}`, {
    shallow: true,
  });
};

/**
 * Returns the display name of the selected facet option.
 * If the selected facet is a size facet, it appends the selected facet display name.
 *
 * @param {Object} params - The parameters for the function.
 * @param {string} params.selectedFacetName - The name of the selected facet.
 * @param {string} params.selectedFacetDisplayName - The display name of the selected facet.
 * @param {string} params.selectedFacetOptionDisplayName - The display name of the selected facet option.
 * @returns {string} - The transformed display name of the selected facet option.
 *
 * @example
 * facetName          | displayName
 * -------------------|-----------------
 * available_size_top | S | 6 (Top Size)
 * tag_color_list     | Red
 */
export const getTransformedFacetOptionDisplayName = ({
  selectedFacetName,
  selectedFacetDisplayName,
  selectedFacetOptionDisplayName,
}) => {
  if (isCioSizeFacet(selectedFacetName)) {
    return `${selectedFacetOptionDisplayName} (${selectedFacetDisplayName})`;
  }

  return selectedFacetOptionDisplayName;
};

export const isolateSizeFacets = (cioFacets = []) => {
  const sizeFacets = [];
  const nonSizeFacets = [];

  cioFacets.forEach((facet) => {
    if (isCioSizeFacet(facet.name)) {
      sizeFacets.push(facet);
    } else {
      nonSizeFacets.push(facet);
    }
  });

  return { sizeFacets, nonSizeFacets };
};

/**
 * Group size facets together in the list of facets.
 * Size facets need to be grouped together for proper UI rendering and functionality.
 * All other facets are preserved in their original order.
 *
 * @param {Array} cioFacets - The list of facets to be grouped.
 * @returns {Array} - The list of facets with size facets grouped together.
 */
export const groupSizeFacets = (cioFacets = []) => {
  const firstSizeFacetIndex = cioFacets.findIndex((facet) =>
    isCioSizeFacet(facet.name)
  );

  const { sizeFacets, nonSizeFacets } = isolateSizeFacets(cioFacets);

  if (!sizeFacets.length) {
    return cioFacets;
  }

  const groupedSizeFacet = {
    display_name: 'Size', // needs to be localized via RB?
    name: 'size',
    sub_facets: sizeFacets,
  };

  return nonSizeFacets?.toSpliced(firstSizeFacetIndex, 0, groupedSizeFacet);
};

/**
 *  We can't use the `selected` directly from cioFacets from the CIO API response, due to size filters
 *  being in cio prefilter expressions instead of the filters param. This results in the size filters
 *  not having the selected value in the response. We have to handle selected filters
 *  by checking the URL params as our source of truth.
 *
 * @param {string} facetName - The name of the facet.
 * @param {string} facetOption - The value of the facet option.
 * @param {Object} selectedUrlFilters - The current URL filters.
 *
 * @returns {boolean} - Returns true if the facet option is selected, false otherwise.
 */
export const isFacetOptionSelected = ({
  facetName,
  facetOption,
  selectedUrlFilters,
}) => {
  if (!selectedUrlFilters || !facetName || !facetOption) {
    return false;
  }

  const urlFacetName = cioFacetsToUrlFilters[facetName] || facetName;
  return Boolean(selectedUrlFilters?.[urlFacetName]?.includes(facetOption));
};

/**
 * Counts the number of selected options in a facet.
 *
 * @param {Object} params - The parameters for the function.
 * @param {string} params.name - The name of the facet.
 * @param {Array} params.subFacets - The sub-facets of the facet. Does not exist for all facets. Mainly for grouped size facets.
 * @param {Array} params.options - The options of the facet.
 * @param {Object} params.selectedUrlFilters - The current URL filters.
 * @returns {number} - The count of selected options.
 */
export const getSelectedOptionCount = ({
  name,
  subFacets,
  options = [],
  selectedUrlFilters,
}) => {
  if (subFacets) {
    return subFacets.reduce((total, subFacet) => {
      return subFacet?.options?.reduce((subTotal, subFacetOption) => {
        return isFacetOptionSelected({
          facetName: subFacet?.name,
          facetOption: subFacetOption?.value,
          selectedUrlFilters,
        })
          ? subTotal + 1
          : subTotal;
      }, total);
    }, 0);
  }

  return options?.reduce((total, option) => {
    return isFacetOptionSelected({
      facetName: name,
      facetOption: option?.value,
      selectedUrlFilters,
    })
      ? total + 1
      : total;
  }, 0);
};

/**
 * Returns the all of the CIO facets based on the URL filters.
 * (Some are only applied via auto-size filters and not via the UI selectors)
 * This is needed because the size filters are not included in the CIO API response,
 * due to them being in the prefilter expressions instead of the filters param.
 * We need to handle selected filters by checking the URL params as our source of truth.
 *
 * Note: Since we are using `allCioFacets` to get the selected size facets, the `count` property
 * is not accurate for the current results of the grid. We are currently not using the `count` property
 * so this is not a problem, but be aware of this technical limitation when using size filters in prefilter expressions.
 *
 * @param {Object} params - The parameters for the function.
 * @param {Object} params.selectedUrlFilters - The current URL filters.
 * @param {Array} params.allCioFacets - The list of all CIO facets.
 * @param {Array} params.cioFacets - The list of CIO facets.
 * @param {string} params.gridType - The type of the grid (e.g., CONSTRUCTOR_SEARCH_GRID).
 * @returns {Array} - The list of selected CIO size facets.
 */
export const getAllCioFacetsWithSelectedUrlFilters = ({
  selectedUrlFilters = {},
  allCioFacets = [],
  cioFacets = [],
  gridType,
}) => {
  const selectedCioSizeFacets = Object.entries(selectedUrlFilters).reduce(
    (acc, [key, value]) => {
      const cioFacetName = urlFiltersToCioFacets[key] || key;
      const cioFacet = allCioFacets?.find(
        (facet) => facet?.name === cioFacetName
      );

      // Ignore all non-size facets
      if (!isCioSizeFacet(cioFacetName) || !cioFacet) {
        return acc;
      }

      // Get the current options for the size facet from the cioFacets (normal API response flow)
      const currentCioFacetOptions = cioFacets?.find(
        (facet) => facet?.name === cioFacetName
      )?.options;

      // Get the selected size options from the URL filters and map them based on the superset of all cioFacets.
      const selectedSizeOptions = value
        ?.map((option) => {
          const cioOption = cioFacet?.options?.find(
            (facetOption) => facetOption?.value === option
          );
          return cioOption;
        })
        ?.filter(Boolean);

      // De-dupe options that are already in the currentCioFacetOptions
      const selectedSizeOptionsNotInCioFacets = selectedSizeOptions?.filter(
        (option) =>
          !currentCioFacetOptions?.some(
            (currentOption) => currentOption?.value === option?.value
          )
      );

      // Append selected size options to the currentCioFacetOptions.
      // By default, selected size options not found in the currentCioFacetOptions
      // will appear at the top of the list.
      const combinedSelectedSizeOptions = [
        ...(selectedSizeOptionsNotInCioFacets || []),
        ...(currentCioFacetOptions || []),
      ];

      return [
        ...acc,
        {
          ...cioFacet,
          options: combinedSelectedSizeOptions,
        },
      ];
    },
    []
  );

  const remainingCioFacets = cioFacets?.filter(
    (facet) =>
      !selectedCioSizeFacets.some(
        (selectedFacet) => selectedFacet.name === facet.name
      )
  );

  const selectedRemainingCioFacets = remainingCioFacets?.filter(
    (facet) =>
      selectedUrlFilters?.[cioFacetsToUrlFilters[facet.name] || facet.name]
  );
  const nonSelectedRemainingCioFacets = remainingCioFacets?.filter(
    (facet) =>
      !selectedUrlFilters?.[cioFacetsToUrlFilters[facet.name] || facet.name]
  );

  // For Search Grid, we prioritize showing selected size facets, then selected non-size facets,
  // then non-selected remaining facets. This is to ensure we keep size facets at the top and render all facets with selected options first,
  // due to the search feature of truncating the list of facets to MAX_FACET_COUNT.
  // For other grid types, we just return the selected size facets and remaining facets in their
  // original order, as the search grid is the only one that truncates the list of facets.
  const orderedCioFacets =
    gridType === GRID_TYPES.CONSTRUCTOR_SEARCH_GRID
      ? [
          ...selectedCioSizeFacets,
          ...selectedRemainingCioFacets,
          ...nonSelectedRemainingCioFacets,
        ]
      : [...selectedCioSizeFacets, ...remainingCioFacets];

  return orderedCioFacets;
};

/**
 * Returns a mapped value from `cioFacetsToUrlFilters` if available
 * Then, removes 'tag_' prefix and '_list' suffix from the given string.
 * Converts result to use kebab case (slugified).
 * Per FLD-2548, Savage brand does process the prefix and suffix.
 * Pending product to align with syncing RBs and data autotags to use the same naming conventions
 * as all other brands.
 *
 * @param {string} filterName - The input string to sanitize.
 * @returns {string} The sanitized string in kebab case.
 */
export const sanitizeFilterName = (filterName) => {
  if (typeof filterName !== 'string') {
    return '';
  }

  // Ensure there was no previous slugification into kebab case for cio to url filters mapping
  const snakeCaseFacetName = filterName.replace(/-/g, '_');

  const mappedFilterName =
    urlFiltersToCioFacets[snakeCaseFacetName] || snakeCaseFacetName;
  const prefixAndSuffixRemoved = mappedFilterName
    .replace(/^tag_/, '')
    .replace(/_list$/, '');

  return getBrandValue({
    savage: slugify(mappedFilterName, '-'),
    default: slugify(prefixAndSuffixRemoved, '-'),
  });
};

/**
 * Generates a slugified version of the selected CIO facet and option display name.
 * This is used to create a unique RB identifier for the selected facet option.
 * Per FLD-2548, Savage brand does not process the prefix and suffix.
 * Pending product to align with syncing RBs and data autotags to use the same naming conventions
 * as all other brands.
 *
 * @param {string} facetName - The name of the facet.
 * @param {string} facetOptionDisplayName - The display name of the facet option.
 * @returns {string} - The slugified version of the selected CIO facet and option display name.
 */
export const getSelectedCioFacetRbid = (facetName, facetOptionDisplayName) => {
  // Ensure there was no previous slugification into kebab case for cio to url filters mapping
  const snakeCaseFacetName = facetName.replace(/-/g, '_');

  const mappedFacetName =
    cioFacetsToUrlFilters[snakeCaseFacetName] || snakeCaseFacetName;
  const prefixAndSuffixRemoved = mappedFacetName
    .replace(/^tag_/, '')
    .replace(/_list$/, '');

  return getBrandValue({
    savage: slugify(`${mappedFacetName}_${facetOptionDisplayName}`, '_'),
    default: slugify(
      `${prefixAndSuffixRemoved}_${facetOptionDisplayName}`,
      '_'
    ),
  });
};

/**
 * Returns the translated display name for a facet option.
 *
 * @param {string} facetName - The name of the facet.
 * @param {Object} facetOption - The facet option object containing `display_name` and `value`.
 * @param {string} domain - The domain to use for translation.
 * @returns {string} - The translated display name for the facet option.
 */
export const getTranslatedFacetOptionDisplayName = ({
  facetName,
  facetOption,
  domain,
}) => {
  const categoryName = cioSizeFacetsToFilterPreferences[facetName];
  let translatedDisplayName = facetOption?.display_name;
  if (categoryName && facetOption?.display_name === facetOption?.value) {
    translatedDisplayName = sizeByKey({
      domain,
      gender: 'F',
      category: categoryName,
      key: facetOption.value,
    });
  }

  const displayName = getBrandValue({
    savage: translatedDisplayName || facetOption?.display_name,
    default: facetOption?.display_name,
  });

  return displayName;
};

/**
 * Returns the initial selected band size value based on the URL filters.
 * If no band size is selected in the URL filters, it returns the first available band size.
 *
 * @param {Object} selectedUrlFilters - The current URL filters.
 * @param {Array} availableBandSizes - The list of available band sizes.
 * @param {string} cioFacetName - The name of the CIO facet for band size.
 * @returns {string} - The initial selected band size value.
 */
export const getInitialSelectedBandSizeValue = ({
  selectedUrlFilters,
  availableBandSizes,
  cioFacetName,
}) => {
  const urlFilterName = cioFacetsToUrlFilters[cioFacetName] || cioFacetName;
  const firstSelectedUrlFilter = selectedUrlFilters?.[urlFilterName]?.[0];
  const parsedBraSizes = parseBraSizes({
    braSizes: firstSelectedUrlFilter ? [firstSelectedUrlFilter] : [],
  });
  const firstSelectedBandSize = Object.keys(parsedBraSizes)?.[0];

  return firstSelectedBandSize || availableBandSizes?.[0]?.value;
};

/**
 * Returns the facets to render based on the grid type.
 * For CONSTRUCTOR_SEARCH_GRID, it filters and sorts the facets to ensure size facets come first
 * and only includes facets with sub-facets, selected options, or more than one option.
 * It also limits the number of facets to MAX_FACET_COUNT.
 * For other grid types, it returns the facets unmodified.
 *
 * @param {Object} params - The parameters for the function.
 * @param {string} params.gridType - The type of the grid.
 * @param {Array} params.facets - The list of facets to filter and sort.
 * @returns {Array} - The filtered and sorted list of facets for the specified grid type
 */
export const getFacetsToRenderByGridType = ({ gridType, facets = [] }) => {
  if (gridType === GRID_TYPES.CONSTRUCTOR_SEARCH_GRID) {
    const sortedFacets = facets?.toSorted((a, b) => {
      if (a?.name === 'size' && b?.name !== 'size') {
        return -1; // Size facets should always come first
      }
      return 0; // Maintain original order for other facets
    });

    const filteredFacets = sortedFacets?.filter((facet) => {
      const isGroupedSizeFacet =
        facet?.name === 'size' && facet?.sub_facets?.length > 0;
      const hasAtLeastOneSelectedOption = facet?.options?.some(
        (option) => option.status === 'selected'
      );
      const hasMoreThanOneOption = facet?.options?.length > 1;

      return (
        isGroupedSizeFacet ||
        hasAtLeastOneSelectedOption ||
        hasMoreThanOneOption
      );
    });

    const facetsWithSelectedOptions = filteredFacets?.filter(
      (facet) =>
        facet?.options?.some((option) => option.status === 'selected') ||
        (facet?.name === 'size' && facet?.sub_facets?.length > 0)
    );

    return facetsWithSelectedOptions?.length > MAX_FACET_COUNT
      ? filteredFacets.slice(0, facetsWithSelectedOptions.length)
      : filteredFacets.slice(0, MAX_FACET_COUNT);
  }

  // By default, return facets unmodified
  return facets;
};
